import { useCallback, useEffect, useReducer, useRef } from 'react';
import { initialPendingState } from 'utils/schedules/layout/initialPendingState';
import { modelsEqual } from 'utils/schedules/layout/modelsEqual';
import { reconcilePending } from 'utils/schedules/layout/reconcilePending';

/**
 * How long to wait, after the last persist resolves, for the store snapshot to
 * confirm the pending model before defensively falling back to store truth.
 */
const SETTLE_TIMEOUT_MS = 10000;

/**
 * Owns the locally-authoritative pending schedule model (Trevor's sequential-edit
 * interaction model: updates must APPEAR instantaneous, and edits never
 * interleave). The pure state machine lives in `reconcilePending.ts`; this hook
 * wires it to React and executes its effects:
 *
 *  - `commitDrop(model, base, changedIntents)` — called synchronously as a drag's
 *    drop completes. The schedule renders `pendingModel` from that instant; the
 *    persist runs in the background, strictly sequentially behind any earlier
 *    drop still settling.
 *  - snapshot reconciliation — every store-derived model is fed to the machine;
 *    the machine settles (snapshot == pending), waits (own echo), or abandons
 *    (external write / failure / timeout), in which case the snapshot wins and
 *    the UI visibly reverts.
 *
 * A schedule change (navigating to another day) resets the machine: pending
 * state never crosses schedule documents.
 */
export function usePendingScheduleModel({ schedule, snapshotModel, persist }) {
  const stateRef = useRef(null);
  const scheduleIdRef = useRef(schedule?.id);
  const scheduleRef = useRef(schedule);
  const persistRef = useRef(persist);
  const watchdogRef = useRef(null);
  const [, forceRender] = useReducer(count => count + 1, 0);

  scheduleRef.current = schedule;
  persistRef.current = persist;

  if (stateRef.current === null || scheduleIdRef.current !== schedule?.id) {
    stateRef.current = initialPendingState(snapshotModel);
    scheduleIdRef.current = schedule?.id;
  }

  const dispatch = useCallback((event) => {
    const { state, effects } = reconcilePending(stateRef.current, event);

    if (state !== stateRef.current) {
      stateRef.current = state;
      forceRender();
    }

    for (const effect of effects) {
      if (effect.type === 'persist') {
        persistRef.current(scheduleRef.current, effect.entry.model, effect.entry.changedIntents)
          .then(() => dispatch({ type: 'persistResolved' }))
          .catch(error => {
            console.error(
              'Failed to persist the rescheduled blocks; the schedule reverts to the last ' +
              'stored layout. TODO: surface this to the user once the app has a notification ' +
              'mechanism. [claude 6.Jul.26]',
              error,
            );
            dispatch({ type: 'persistFailed' });
          });
      } else if (effect.type === 'abandon' && effect.reason === 'external') {
        console.warn(
          'usePendingScheduleModel: the schedule changed outside this view while a drop was ' +
          'settling; the external change wins and local pending edits were discarded.'
        );
      } else if (effect.type === 'abandon' && effect.reason === 'timeout') {
        console.warn(
          'usePendingScheduleModel: the store never confirmed a persisted drop within ' +
          `${SETTLE_TIMEOUT_MS}ms; falling back to the stored layout.`
        );
      }
      // reason === 'failure' is already logged where the persist rejects.
    }
  }, []);

  // Feed every store-derived model change to the machine. The reducer returns the
  // same state reference (no re-render) when the snapshot is old news.
  useEffect(() => {
    if (snapshotModel && !modelsEqual(stateRef.current.lastSnapshot, snapshotModel)) {
      dispatch({ type: 'snapshot', model: snapshotModel });
    }
  }, [snapshotModel, dispatch]);

  // Settle watchdog: armed whenever a pending model is waiting ONLY on a snapshot
  // (nothing in flight, nothing queued).
  const { pending, inFlight, queue } = stateRef.current;
  const awaitingSettle = !!pending && !inFlight && queue.length === 0;

  useEffect(() => {
    if (!awaitingSettle) {
      return undefined;
    }

    watchdogRef.current = setTimeout(() => dispatch({ type: 'timeout' }), SETTLE_TIMEOUT_MS);

    return () => clearTimeout(watchdogRef.current);
  }, [awaitingSettle, pending, dispatch]);

  const commitDrop = useCallback((model, base, changedIntents) => {
    if (!persistRef.current) {
      console.error(
        'Scheduler: the onDragPersist prop is required to persist drags atomically; ' +
        'the drop was discarded.'
      );
      return;
    }

    dispatch({ type: 'drop', model, base, changedIntents });
  }, [dispatch]);

  return {
    pendingModel: stateRef.current.pending,
    commitDrop,
  };
}
